import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Minimize01,
	Trash01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { DeleteTicketModal } from "@/components/Organisms/DeleteTicketModal/DeleteTicketModal";
import { DetailsState } from "@/components/Organisms/Details/Details";
import {
	PageHeader,
	type PageHeaderTab,
} from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Sidebar } from "@/components/Organisms/Sidebar/Sidebar";
import { TicketSidebarDetails } from "@/components/Organisms/TicketSidebarDetails/TicketSidebarDetails";
import {
	TicketFormSaveProvider,
	useTicketFormSaveRegistry,
} from "@/features/tickets/components/ticket-form-save-context";
import {
	commitAutosaveOrStay,
	useTicketDetailsSaveFailLabels,
} from "@/features/tickets/hooks/use-ticket-details-navigation-guard";
import { useTicketFormNavigationGuard } from "@/features/tickets/hooks/use-ticket-form-navigation-guard";
import {
	getWorkspaceTabId,
	WorkspaceTab,
	type WorkspaceTabId,
} from "@/features/tickets/lib/get-workspace-tab-id.util";
import { isNestedAssetWorkspacePath } from "@/features/tickets/lib/is-nested-asset-workspace-path.util";
import { isNestedRecordWorkspacePath } from "@/features/tickets/lib/is-nested-record-workspace-path.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import { ticketQueryOptions } from "@/services/ticket";
import { cx } from "@/utils/cx";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

/**
 * Ticket Workspace shell — second-level entity page (Landing → Drawer → Workspace).
 * Header: PageHeader with section tabs, Options overflow (delete), and collapse back to drawer.
 */
export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/workspace",
)({
	component: TicketWorkspaceLayout,
});

function TicketWorkspaceLayout() {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});

	if (
		isNestedRecordWorkspacePath(pathname) ||
		isNestedAssetWorkspacePath(pathname)
	) {
		return <Outlet />;
	}

	return (
		<TicketFormSaveProvider>
			<TicketWorkspaceLayoutInner />
		</TicketFormSaveProvider>
	);
}

function TicketWorkspaceLayoutInner() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/workspace",
	});
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const selectedTabId = getWorkspaceTabId(pathname);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);

	const formSaveRegistry = useTicketFormSaveRegistry();
	const saveFailLabels = useTicketDetailsSaveFailLabels();
	useTicketFormNavigationGuard();

	const runWithFormCommit = useCallback(
		async (action: () => void) => {
			const ctrl = formSaveRegistry?.getController() ?? null;
			if (ctrl?.hasPending()) {
				const shouldStay = await commitAutosaveOrStay(
					ctrl.commitPending,
					saveFailLabels,
				);
				if (shouldStay) return;
			}
			action();
		},
		[formSaveRegistry, saveFailLabels],
	);

	const ticketById = useQuery(
		ticketQueryOptions.detail(params.ticketId, {
			organizationId: params.orgId,
		}),
	);

	const canDeleteTickets = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteTicket",
			entityType: "Ticket",
			entityId: params.ticketId,
		}),
		enabled: !!params.ticketId,
	});

	const title = ticketById.data?.name ?? t("main.tickets.workspace.title");

	const tabs: PageHeaderTab[] = useMemo(
		() => [
			{
				id: WorkspaceTab.Form,
				label: t("main.tickets.workspace.tabs.form"),
			},
			{
				id: WorkspaceTab.Records,
				label: t("main.tickets.workspace.tabs.records"),
			},
			{
				id: WorkspaceTab.Assets,
				label: t("main.tickets.workspace.tabs.assets"),
			},
			{
				id: WorkspaceTab.Attachments,
				label: t("main.tickets.workspace.tabs.attachments"),
			},
			{
				id: WorkspaceTab.Notes,
				label: t("main.tickets.workspace.tabs.notes"),
			},
			{
				id: WorkspaceTab.Map,
				label: t("main.tickets.workspace.tabs.map"),
			},
		],
		[t],
	);

	const metadata = useMemo(() => {
		const ticket = ticketById.data;
		if (!ticket) return [];

		const items: {
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (ticket.ticketNumber != null) {
			items.push({
				label: String(ticket.ticketNumber),
				icon: Hash01,
			});
		}
		if (ticket.createdAt) {
			items.push({
				label: createdMetadataLabel(t, ticket.createdAt),
				icon: Calendar,
			});
		}
		if (ticket.updatedAt) {
			items.push({
				label: updatedMetadataLabel(t, ticket.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [ticketById.data, t]);

	const handleBackToJobs = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/tickets",
				params: { orgId: params.orgId },
			});
		});
	};

	const handleCollapseWorkspace = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/tickets/$ticketId/details",
				params: { orgId: params.orgId, ticketId: params.ticketId },
				search: { mode: "view" },
			});
		});
	};

	const handleTabChange = (tabId: string) => {
		const next = tabId as WorkspaceTabId;
		if (next === selectedTabId) return;

		void runWithFormCommit(() => {
			navigate({
				to: `/web/$orgId/tickets/$ticketId/workspace/${next}`,
				params: { orgId: params.orgId, ticketId: params.ticketId },
			});
		});
	};

	const handleDelete = () => {
		setIsDeleteModalOpen(true);
	};

	const sidebarLabel = t("main.tickets.workspace.sidebarLabel");

	return (
		<div className="relative flex min-h-0 w-full flex-1 overflow-hidden">
			<div
				className={cx(
					"flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-300 ease-in-out motion-reduce:transition-none",
					isSidebarOpen && "lg:pr-[440px]",
				)}
			>
				<PageLayout
					header={
						<PageHeader
							type="ticket"
							title={title}
							breadcrumbType="single"
							breadcrumbs={[
								{
									label: t("main.tickets.workspace.backToTickets"),
									onPress: handleBackToJobs,
								},
							]}
							metadata={metadata.length > 0 ? metadata : undefined}
							overflowAriaLabel={t("common.tableActions.options")}
							overflowActions={
								canDeleteTickets.data
									? [
											{
												label: t("main.tickets.drawer.deleteTicket.button"),
												icon: Trash01,
												onPress: handleDelete,
											},
										]
									: []
							}
							actions={[
								{
									label: t("main.tickets.workspace.action"),
									"aria-label": t("main.tickets.workspace.collapse"),
									icon: Minimize01,
									color: "secondary",
									onPress: handleCollapseWorkspace,
								},
							]}
							showTabs
							tabs={tabs}
							selectedTabId={selectedTabId}
							onTabChange={handleTabChange}
						/>
					}
				>
					<Outlet />
				</PageLayout>
			</div>
			<Sidebar
				isOpen={isSidebarOpen}
				onOpenChange={setIsSidebarOpen}
				aria-label={sidebarLabel}
				className="max-lg:hidden"
			>
				<TicketSidebarDetails
					orgId={params.orgId}
					ticketId={params.ticketId}
					state={
						ticketById.isPending ? DetailsState.Loading : DetailsState.Default
					}
				/>
			</Sidebar>
			<DeleteTicketModal
				isOpen={isDeleteModalOpen}
				setIsOpen={setIsDeleteModalOpen}
				handleClose={() => setIsDeleteModalOpen(false)}
				onDeleteSuccess={() => {
					setIsDeleteModalOpen(false);
					navigate({
						to: "/web/$orgId/tickets",
						params: { orgId: params.orgId },
						replace: true,
					});
				}}
			/>
		</div>
	);
}
